/**
 * Provides an administrative interface for managing role-based widget access,
 * allowing administrators to view, modify, and save access permissions through
 * an interactive access matrix.
 */
import React, { useEffect, useState } from "react";

import { ROLE_NAME_MAP, WIDGET_NAME_MAP } from "@/shared/utils/maps";
import { MatrixRow, Notice, NoticeType, Role } from "../../types/access";
import { getAccessMatrix, saveAccessChanges } from "../../services/accessService";



const InlineNotice: React.FC<{
  notice: Notice;
  onClose: (id: number) => void;
}> = ({ notice, onClose }) => {
  const styles: Record<NoticeType, string> = {
    success: "bg-orange-50 border-orange-200 text-orange-900",
    error: "bg-rose-50 border-rose-200 text-rose-900",
    info: "bg-orange-50 border-orange-200 text-orange-900",
  };

  return (
    <div
      className={`w-full rounded-md border p-3 text-sm flex justify-between ${styles[notice.type]}`}
    >
      <span>{notice.message}</span>
      <button onClick={() => onClose(notice.id)}>✕</button>
    </div>
  );
};

const AccessMatrix: React.FC = () => {
  const [matrix, setMatrix] = useState<MatrixRow[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [initialMatrix, setInitialMatrix] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notices, setNotices] = useState<Notice[]>([]);

  const pushNotice = (type: NoticeType, message: string) =>
    setNotices((p) => [...p, { id: Date.now(), type, message }]);

  const closeNotice = (id: number) => setNotices((p) => p.filter((n) => n.id !== id));

  useEffect(() => {
    loadMatrix();
  }, []);

  const loadMatrix = async () => {
    setLoading(true);
    try {
      const data = await getAccessMatrix();
      setMatrix(data.matrix);
      setRoles(data.roles);
      setInitialMatrix(JSON.stringify(data.matrix));
    } catch {
      pushNotice("error", "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const toggleAccess = (rowIndex: number, roleIndex: number) => {
    setMatrix((prev) => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      row.access = row.access.map((v, i) => (i === roleIndex ? !v : v));
      next[rowIndex] = row;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAccessChanges({ matrix, roles, updatedBy: "Admin" });
      setInitialMatrix(JSON.stringify(matrix));
      pushNotice("success", "Changes saved");
    } catch {
      pushNotice("error", "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(matrix) !== initialMatrix;

  if (loading) return <div className="p-5">Loading...</div>;

  return (
    <div className="bg-[#f6f4ef] min-h-screen font-sans">
      <div className="p-5">
        {notices.map((n) => (
          <div key={n.id} className="mb-2">
            <InlineNotice notice={n} onClose={closeNotice} />
          </div>
        ))}

        <div className="bg-white border border-gray-200 rounded-md">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-4">
            <div className="flex-1">
              <h2 className="text-md font-semibold text-gray-800">Access Matrix</h2>
            </div>

            {hasChanges && (
              <div className="text-orange-600 border border-orange-300 bg-orange-50 px-3 py-1 rounded-md text-sm">
                Unsaved
              </div>
            )}

            <button
              className="bg-[#e7e5e0] text-gray-700 px-4 py-1.5 rounded-md text-sm"
              onClick={loadMatrix}
            >
              Cancel
            </button>

            <button
              className="bg-[var(--color-brand)]  text-white px-4 py-1.5 rounded-md text-sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>

          <div className="overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-[#fbfaf7]">
                <tr>
                  <th className="border border-gray-200 p-3 text-left font-medium text-gray-700">
                    <div className="flex items-center gap-2">Widget Name</div>
                  </th>

                  {roles.map((r) => (
                    <th
                      key={r.role_id}
                      className="border border-gray-200 p-3 text-center font-medium text-gray-700"
                    >
                      {ROLE_NAME_MAP[r.role_code] || r.role_code}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {matrix
                  .filter((row) => row.feature_code !== row.domain)
                  .map((row) => {
                    const rowIndex = matrix.findIndex((m) => m.feature_id === row.feature_id);

                    const widgetName = WIDGET_NAME_MAP[row.feature_code] || row.feature_code;

                    return (
                      <tr key={row.feature_id} className="hover:bg-[#f7f5f1]">
                        <td className="border border-gray-200 p-3 text-sm text-gray-800 font-medium capitalize">
                          {widgetName}
                        </td>

                        {row.access.map((allowed, i) => (
                          <td
                            key={i}
                            className={`border border-gray-200 p-3 text-center cursor-pointer ${
                              allowed ? "bg-[#fff4ec]" : "bg-[#fbfaf7]"
                            }`}
                            onClick={() => toggleAccess(rowIndex, i)}
                          >
                            {allowed && (
                              <span className="text-orange-500 text-[16px] font-semibold">✓</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessMatrix;
